import { useTranslation } from 'react-i18next';
import {
	GovControlGroup,
	GovDropdown,
	GovIcon,
} from '@gov-design-system-ce/react';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import { useAuthorizedLabel } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useEPeticeDetailContext } from '@gov-nx/module/page';
import { Button, Dialog } from '@gov-nx/ui/web';
import { SouhlasCheckbox } from '../../../Autorizace/SouhlasCheckbox';
import { MarkdownRender } from '../../../Markdown/MarkdownRender';

interface EPetitionCloseSubmitButtonsProps {
	code: PageCode;
}

const SUBMIT_CLOSE_FORM_ID = 'EPETICE_SUBMIT_CLOSE_FORM';

export const EPetitionCloseSubmitButtons = ({
	code,
}: EPetitionCloseSubmitButtonsProps) => {
	const { t } = useTranslation(code);
	const authLabel = useAuthorizedLabel();
	const {
		controls,
		isDropdownOpen,
		ePetitionSubmitClose,
		handleEPetitionClose,
		handleSubmitCloseSubmit,
		submitCloseFormDefinition,
		setControls,
		onCloseModal,
	} = useEPeticeDetailContext();

	const closeSubmitLabel = authLabel(
		t('tlacitko.akce.uzavrit-podat'),
		ePetitionSubmitClose.isAuthorizationEnforcedPodat
	);

	return (
		<div className="flex flex-start items-center gap-4">
			<GovControlGroup
				color="primary"
				fitContent={true}>
				<Button
					onClick={() => {
						setControls({ isCloseAndSubmitModal: true });
					}}
					color="primary"
					type="solid"
					size="m"
					aria-label={t('akce.uzavrit-podat')}>
					{t('akce.uzavrit-podat')}
				</Button>
				<GovDropdown
					color="primary"
					type="solid"
					size="m"
					aria-label="akce.wcag.rozbalit"
					disabled={controls.isSubmitAndCloseProcessing}
					open={isDropdownOpen.value}
					onChange={(status: boolean) => isDropdownOpen.setValue(status)}
					position="right"
					label={
						<GovIcon
							name="chevron-down"
							slot="icon-start"
						/>
					}>
					<Button
						onClick={() => {
							setControls({ isCloseModal: true });
						}}
						color="neutral"
						type="base"
						size="m"
						aria-label={t('akce.uzavrit')}>
						{t('akce.uzavrit')}
					</Button>
				</GovDropdown>
			</GovControlGroup>
			<Dialog
				isOpen={controls.isCloseAndSubmitModal}
				onClose={() => {
					setControls({ isCloseAndSubmitModal: false });
					submitCloseFormDefinition.formMethods.reset();
				}}
				title={<h3>{t('modal.pole.uzavrit-podat')}</h3>}
				footer={
					<>
						<Button
							form={SUBMIT_CLOSE_FORM_ID}
							nativeType="submit"
							isLoading={controls.isSubmitAndCloseProcessing}
							isDisabled={controls.isSubmitAndCloseProcessing}
							color="primary"
							type="solid"
							size="s"
							aria-label={closeSubmitLabel}>
							{closeSubmitLabel}
						</Button>
						<Button
							onClick={() => {
								setControls({ isCloseAndSubmitModal: false });
								submitCloseFormDefinition.formMethods.reset();
							}}
							color="error"
							nativeType="button"
							size="s"
							type="outlined"
							aria-label={t('tlacitko.akce.neuzavirat')}>
							{t('tlacitko.akce.neuzavirat')}
						</Button>
					</>
				}>
				<GovFormWeb
					id={SUBMIT_CLOSE_FORM_ID}
					formDefinition={submitCloseFormDefinition}
					onSubmit={handleSubmitCloseSubmit}>
					<MarkdownRender>
						{t('modal.pole.chcete-uzavrit-podat')}
					</MarkdownRender>
					<SouhlasCheckbox
						show={ePetitionSubmitClose.isLowAuthorizationPodat}
						disabled={controls.isSubmitAndCloseProcessing}
					/>
				</GovFormWeb>
			</Dialog>
			<Dialog
				isOpen={controls.isCloseModal}
				onClose={() => onCloseModal()}
				title={<h3>{t('modal.pole.pouze-uzavrit')}</h3>}
				footer={
					<>
						<Button
							onClick={() => handleEPetitionClose()}
							isLoading={controls.isCloseProcessing}
							isDisabled={controls.isCloseProcessing}
							color="primary"
							type="solid"
							nativeType="button"
							size="s"
							aria-label={t('tlacitko.akce.pouze-uzavrit')}>
							{t('tlacitko.akce.pouze-uzavrit')}
						</Button>
						<Button
							onClick={() => onCloseModal()}
							color="error"
							nativeType="button"
							size="s"
							type="outlined"
							aria-label={t('tlacitko.akce.neuzavirat')}>
							{t('tlacitko.akce.neuzavirat')}
						</Button>
					</>
				}>
				<MarkdownRender>{t('modal.pole.chcete-pouze-uzavrit')}</MarkdownRender>
			</Dialog>
		</div>
	);
};
